import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articleContent } from "@/mocks/article";

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function GuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20workspace%20scene%20with%20an%20open%20laptop%20on%20a%20clean%20wooden%20desk%20displaying%20abstract%20colorful%20data%20alongside%20a%20notebook%20a%20ceramic%20coffee%20mug%20and%20a%20small%20potted%20succulent%20plant%20soft%20natural%20morning%20daylight%20creating%20a%20calm%20productive%20atmosphere%20warm%20cream%20and%20beige%20tones%20clean%20editorial%20photography%20no%20text%20no%20people%20sophisticated%20cozy%20vibe&width=1800&height=700&seq=kakobuy-guide-hero-v3-2026&orientation=landscape"
              alt="How to Use Kakobuy Spreadsheet Guide"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-white/70 text-xs font-medium mb-4">
                <Link to="/" className="hover:text-white transition-colors">
                  Home
                </Link>
                <span>/</span>
                <span className="text-white/90">Guide</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                {articleContent.title}
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
                {articleContent.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {articleContent.sections.map((section, idx) => (
                <div key={idx} className="mb-12 md:mb-14">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 leading-tight">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              ))}

              {/* Back to Home CTA */}
              <div className="mt-14 pt-10 border-t border-background-200/70 text-center">
                <p className="text-sm text-foreground-600 mb-4">
                  Ready to start shopping with verified sellers?
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md hover:bg-primary-600 transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Kakobuy Spreadsheet
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Guides */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                  <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                    Continue Reading
                  </p>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    Related Guides
                  </h2>
                </div>
                <Link
                  to="/"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  View All &rarr;
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articleContent.relatedGuides.map((guide, idx) => (
                  <Link
                    key={idx}
                    to={guide.slug}
                    className="group block bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                  >
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {guide.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                      Read More <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Article Schema - Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": articleContent.title,
            "description": articleContent.subtitle,
            "author": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet",
              "url": siteUrl,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet",
              "url": siteUrl,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
              },
            },
            "datePublished": "2026-06-25",
            "dateModified": "2026-06-25",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": siteUrl + "/guide/how-to-use-kakobuy-spreadsheet",
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl,
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "How to Use Kakobuy Spreadsheet Guide",
                "item": siteUrl + "/guide/how-to-use-kakobuy-spreadsheet",
              },
            ],
          }),
        }}
      />
    </>
  );
}